import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { FaCloudUploadAlt } from '@react-icons/all-files/fa/FaCloudUploadAlt';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { FaDownload } from '@react-icons/all-files/fa/FaDownload';
import { FaMagic } from '@react-icons/all-files/fa/FaMagic';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaFileAlt } from '@react-icons/all-files/fa/FaFileAlt';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';
import { getAuthHeaders } from '../services/auth';

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 1200px;
  margin: 0 auto;
`;

const UploadIcon = styled.div`
  font-size: 48px;
  color: #2563eb;
  margin-bottom: 20px;
`;

const UploadTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
`;

const UploadDescription = styled.p`
  color: #4b5563;
  text-align: center;
  margin-bottom: 20px;
`;

const UploadButton = styled(motion.button)`
  background-color: #2563eb;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #1d4ed8;
  }

  &:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const StatusMessage = styled.div<{ error?: boolean }>`
  margin-top: 20px;
  color: ${props => props.error ? '#dc2626' : '#059669'};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UploadStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 20px 0;
  width: 100%;
`;

const ProgressBar = styled(motion.div)`
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  margin: 20px 0;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background-color: #2563eb;
  border-radius: 4px;
`;

const StatusText = styled.div`
  color: #4b5563;
  font-size: 14px;
  text-align: center;
  width: 100%;
`;

const DownloadLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  text-decoration: none;
  margin-top: 20px;
  padding: 12px 24px;
  background-color: #f3f4f6;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s;
  border: 1px solid #e5e7eb;

  &:hover {
    color: #1d4ed8;
    background-color: #e5e7eb;
    text-decoration: none;
    transform: translateY(-2px);
  }
`;

const TextContainer = styled.div`
  width: 100%;
  margin-top: 20px;
  background-color: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const TextLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TextContent = styled.div`
  font-size: 15px;
  line-height: 1.8;
  color: #334155;
  white-space: pre-wrap;
  padding: 20px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  width: 100%;
  max-height: none;
  overflow: visible;
`;

const EditableTextContent = styled.textarea`
  font-size: 15px;
  line-height: 1.8;
  color: #334155;
  white-space: pre-wrap;
  padding: 20px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
  resize: vertical;
  min-height: 200px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const SubmitButton = styled(motion.button)`
  background-color: #2563eb;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background-color: #1d4ed8;
    transform: translateY(-2px);
  }

  &:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
    transform: none;
  }
`;

const ComparisonContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 16px;
`;

const ComparisonColumn = styled.div`
  flex: 1;
  padding: 12px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

const ComparisonTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  text-align: center;
`;

const ComparisonContent = styled.div`
  line-height: 1.6;
  color: #334155;
`;

const ScoreBreakdownContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 20px 0;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ScoreCategory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const CategoryTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
`;

const ScoreItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ScoreLabel = styled.span`
  color: #475569;
  font-size: 14px;
`;

const ScoreValue = styled.span<{ score: number; max: number }>`
  font-weight: 600;
  color: ${props => {
    const percentage = (props.score / props.max) * 100;
    if (percentage >= 80) return '#059669';
    if (percentage >= 60) return '#2563eb';
    return '#dc2626';
  }};
`;

const GradeScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: 0 auto 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const GradeScoreHeader = styled.div`
  padding: 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  text-align: center;
`;

const GradeScoreTitle = styled.h3`
  font-size: 18px;
  color: #1e293b;
  font-weight: 600;
  margin: 0;
`;

const GradeScoreContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: #e2e8f0;
`;

const GradeScoreItem = styled.div`
  background: white;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const GradeScoreLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const GradeScoreValue = styled.div<{ isGrade?: boolean }>`
  font-size: ${props => props.isGrade ? '64px' : '56px'};
  font-weight: 800;
  color: ${props => props.isGrade ? '#2563eb' : '#059669'};
  line-height: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const ScoreMax = styled.span`
  font-size: 24px;
  color: #94a3b8;
  font-weight: 500;
`;

interface MarkingResult {
  grade: string;
  score: number;
  scoreBreakdown: {
    content: {
      theme: number;
      depth: number;
      structure: number;
    };
    language: {
      vocabulary: number;
      sentence: number;
      rhetoric: number;
    };
    grammar: {
      grammar: number;
      punctuation: number;
    };
  };
  feedback: {
    comment: string;
    recommendation: string;
    grammarCorrections: string;
    fullText: string;
  };
}

interface FileUploadProps {
  onUploadSuccess: (fileInfo: { fileUrl: string; extractedText: string[] }) => void;
  onUploadError: (error: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onUploadSuccess, onUploadError }) => {
  const { t, i18n } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{ message: string; error?: boolean } | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState<string[]>([]);
  const [processedText, setProcessedText] = useState<string[]>([]);
  const [editedText, setEditedText] = useState<string>('');
  const [compositionTopic, setCompositionTopic] = useState<string>('');
  const [markingResult, setMarkingResult] = useState<MarkingResult | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [markingProgress, setMarkingProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getStageTitle = () => {
    if (isUploading) return t('uploadingTitle');
    if (isProcessing) return t('processingTitle');
    if (isMarking) return t('markingTitle');
    if (markingResult) return t('markingCompleteTitle');
    if (uploadedFileUrl) return t('editTitle');
    return t('uploadTitle');
  };

  const getStageDescription = () => {
    if (isUploading) return t('uploadingDescription');
    if (isProcessing) return t('processingDescription');
    if (isMarking) return t('markingDescription');
    if (markingResult) return t('markingCompleteDescription');
    if (uploadedFileUrl) return t('editDescription');
    return t('uploadDescription');
  };

  const getStageIcon = () => {
    if (isUploading) return <FaCloudUploadAlt />;
    if (isProcessing) return <FaSpinner className="fa-spin" />;
    if (isMarking) return <FaMagic />;
    if (markingResult) return <FaCheckCircle />;
    if (uploadedFileUrl) return <FaEdit />;
    return <FaCloudUploadAlt />;
  };

  const processTextWithDeepseek = async (texts: string[]) => {
    setIsProcessing(true);
    setProcessingProgress(0);
    setUploadStatus({ message: t('processingStatus') });

    try {
      const progressInterval = setInterval(() => {
        setProcessingProgress(prev => {
          const newProgress = prev + 1;
          if (newProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return newProgress;
        });
      }, 600);

      const response = await fetch(API_ENDPOINTS.DEEPSEEK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [
            {
              role: "user",
              content: texts.join("\n\n")
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? `請從上述文本中提取作文題目和正文內容。你的回答必須且只能是一個有效的 JSON 對象，不能包含任何其他文字、前綴或說明。

確保：
1. 提取作文題目（如果有的話）
2. 提取並整理作文正文內容
3. 適當分段
4. 使用正確的標點符號
5. 保持一致的格式
6. 移除任何非作文內容（如頁碼、標題等）

重要提示：
1. 回答必須且只能是一個有效的 JSON 對象
2. 不能包含任何其他文字、前綴或說明
3. 不能包含任何 JSON 相關的標記（如 json、\`\`\`json 等）
4. 不能包含任何格式化的前綴或後綴
5. 直接返回 JSON 對象，不要有任何包裝

回答格式必須完全符合以下 JSON 格式：
{
  "topic": "作文題目（如果沒有則返回空字符串）",
  "content": "整理後的作文正文內容"
}`
                : `Please extract the composition topic and content from the above text. Your response must be ONLY a valid JSON object, with no additional text, prefixes, or explanations.

Ensure:
1. Extract the composition topic (if present)
2. Extract and format the main content
3. Proper paragraph spacing
4. Correct punctuation
5. Consistent formatting
6. Remove any non-composition content (like page numbers, headers, etc.)

Important:
1. Response must be ONLY a valid JSON object
2. No additional text, prefixes, or explanations allowed
3. Do not include any JSON-related markers (like json, \`\`\`json, etc.)
4. Do not include any formatting prefixes or suffixes
5. Return the JSON object directly, with no wrapping

Response must strictly follow this JSON format:
{
  "topic": "composition topic (empty string if none)",
  "content": "formatted composition content"
}`
            }
          ],
          stream: false
        }),
      });

      if (!response.ok) {
        throw new Error(t('processingError'));
      }

      const result = await response.json();
      if (result.output && result.output.content) {
        try {
          const parsedResult = JSON.parse(result.output.content.trim());
          if (typeof parsedResult.topic === 'string' && typeof parsedResult.content === 'string') {
            setExtractedText([parsedResult.content]);
            setProcessedText([parsedResult.content]);
            setCompositionTopic(parsedResult.topic);
            setUploadStatus({ message: t('processingComplete') });
          } else {
            throw new Error(t('invalidResponseError'));
          }
        } catch (error) {
          console.error('Parsing error:', error);
          throw new Error(t('invalidResponseError'));
        }
      } else {
        throw new Error(t('invalidResponseError'));
      }

      clearInterval(progressInterval);
      setProcessingProgress(100);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('processingError');
      setUploadStatus({ message: errorMessage, error: true });
      onUploadError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
        setSelectedFile(file);
        setUploadStatus(null);
        setUploadedFileUrl(null);
        await handleUpload(file);
      } else {
        setUploadStatus({ message: 'Please select a PDF or image file', error: true });
      }
    }
  };

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus({ message: 'Preparing upload...' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress for 1 minute
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + 1;
          if (newProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return newProgress;
        });
      }, 600); // 600ms * 100 = 60 seconds

      setUploadStatus({ message: 'Uploading file...' });
      const response = await fetch(API_ENDPOINTS.OCR, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      if (!data.fileUrl) {
        throw new Error('Invalid response format from server');
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadedFileUrl(data.fileUrl);
      
      // Process the extracted text with Deepseek to separate topic and content
      if (data.extractedText && data.extractedText.length > 0) {
        setIsUploading(() => false);
        await processTextWithDeepseek(data.extractedText);
      }

      onUploadSuccess({
        fileUrl: data.fileUrl,
        extractedText: data.extractedText || []
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setUploadStatus({ message: errorMessage, error: true });
      onUploadError(errorMessage);
    } finally {
      setIsUploading(() => false);
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedText(event.target.value);
  };

  const handleSubmit = async () => {
    setIsMarking(true);
    setMarkingProgress(0);
    setUploadStatus({ message: t('markingStatus') });

    try {
      // Start progress simulation
      const progressInterval = setInterval(() => {
        setMarkingProgress(prev => {
          const newProgress = prev + 1;
          if (newProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return newProgress;
        });
      }, 600); // 600ms * 100 = 60 seconds

      const response = await fetch(API_ENDPOINTS.DEEPSEEK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '作文題目：' + (compositionTopic || '未提供') + '\n\n作文內容：\n' + (editedText || processedText.join('\n\n'))
                : 'Composition Topic: ' + (compositionTopic || 'Not provided') + '\n\nComposition Content:\n' + (editedText || processedText.join('\n\n'))
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '你是一位專業且嚴格的中文作文評分老師。請根據以下標準對這篇作文進行評分和批改。'
                : 'You are a professional and strict composition marking teacher. Please evaluate this composition based on the following criteria.'
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '評分標準：\n' +
                  '1. 內容（40分）：\n' +
                  '   - 主題是否明確且切題（15分）\n' +
                  '   - 內容是否充實且有深度（15分）\n' +
                  '   - 結構是否完整，段落是否連貫（10分）\n' +
                  '2. 語言表達（40分）：\n' +
                  '   - 用詞是否準確、豐富（15分）\n' +
                  '   - 句式是否多樣、流暢（15分）\n' +
                  '   - 修辭是否恰當、生動（10分）\n' +
                  '3. 文法和標點（20分）：\n' +
                  '   - 語法是否正確（10分）\n' +
                  '   - 標點符號使用是否準確（10分）'
                : 'Scoring Criteria:\n' +
                  '1. Content (40 points):\n' +
                  '   - Clear and relevant theme (15 points)\n' +
                  '   - Rich and in-depth content (15 points)\n' +
                  '   - Complete structure and coherent paragraphs (10 points)\n' +
                  '2. Language Expression (40 points):\n' +
                  '   - Accurate and rich vocabulary (15 points)\n' +
                  '   - Varied and fluent sentence structures (15 points)\n' +
                  '   - Appropriate and vivid rhetoric (10 points)\n' +
                  '3. Grammar and Punctuation (20 points):\n' +
                  '   - Correct grammar (10 points)\n' +
                  '   - Accurate punctuation (10 points)'
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '評分要求：\n' +
                  '1. 嚴格按照上述標準評分\n' +
                  '2. 優秀作文（90-100分）：各方面表現出色，幾乎沒有錯誤\n' +
                  '3. 良好作文（80-89分）：整體表現良好，有少量小錯誤\n' +
                  '4. 中等作文（70-79分）：基本達到要求，但存在明顯不足\n' +
                  '5. 及格作文（60-69分）：勉強達到基本要求\n' +
                  '6. 不及格作文（60分以下）：未能達到基本要求'
                : 'Scoring Requirements:\n' +
                  '1. Strictly follow the above criteria\n' +
                  '2. Excellent (90-100): Outstanding in all aspects, almost no errors\n' +
                  '3. Good (80-89): Overall good performance, minor errors\n' +
                  '4. Average (70-79): Meets basic requirements but with noticeable shortcomings\n' +
                  '5. Pass (60-69): Barely meets basic requirements\n' +
                  '6. Fail (below 60): Fails to meet basic requirements'
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? 'HKDSE 評分標準補充：\n' +
                  '1. 內容評分（40分）：\n' +
                  '   - 主題明確性（15分）：\n' +
                  '     * 是否緊扣題目要求\n' +
                  '     * 是否展現個人見解\n' +
                  '     * 是否具備獨特觀點\n' +
                  '   - 內容深度（15分）：\n' +
                  '     * 論點是否充分\n' +
                  '     * 論據是否充實\n' +
                  '     * 分析是否深入\n' +
                  '   - 結構組織（10分）：\n' +
                  '     * 段落是否分明\n' +
                  '     * 層次是否清晰\n' +
                  '     * 過渡是否自然\n\n' +
                  '2. 語言表達（40分）：\n' +
                  '   - 詞彙運用（15分）：\n' +
                  '     * 用詞是否準確\n' +
                  '     * 詞彙是否豐富\n' +
                  '     * 是否恰當使用成語\n' +
                  '   - 句式結構（15分）：\n' +
                  '     * 句式是否多變\n' +
                  '     * 表達是否流暢\n' +
                  '     * 是否善用修辭\n' +
                  '   - 修辭技巧（10分）：\n' +
                  '     * 修辭是否恰當\n' +
                  '     * 表達是否生動\n' +
                  '     * 文采是否優美\n\n' +
                  '3. 文法和標點（20分）：\n' +
                  '   - 語法正確性（10分）：\n' +
                  '     * 語法是否規範\n' +
                  '     * 表達是否清晰\n' +
                  '     * 邏輯是否嚴謹\n' +
                  '   - 標點運用（10分）：\n' +
                  '     * 標點是否準確\n' +
                  '     * 格式是否規範\n' +
                  '     * 分段是否合理'
                : 'HKDSE Scoring Standards:\n' +
                  '1. Content (40 points):\n' +
                  '   - Theme Clarity (15 points):\n' +
                  '     * Relevance to topic\n' +
                  '     * Personal insights\n' +
                  '     * Unique perspectives\n' +
                  '   - Content Depth (15 points):\n' +
                  '     * Sufficient arguments\n' +
                  '     * Rich evidence\n' +
                  '     * In-depth analysis\n' +
                  '   - Structure (10 points):\n' +
                  '     * Clear paragraphs\n' +
                  '     * Logical hierarchy\n' +
                  '     * Smooth transitions\n\n' +
                  '2. Language Expression (40 points):\n' +
                  '   - Vocabulary (15 points):\n' +
                  '     * Accurate word choice\n' +
                  '     * Rich vocabulary\n' +
                  '     * Appropriate idioms\n' +
                  '   - Sentence Structure (15 points):\n' +
                  '     * Varied sentences\n' +
                  '     * Fluent expression\n' +
                  '     * Effective rhetoric\n' +
                  '   - Rhetorical Skills (10 points):\n' +
                  '     * Appropriate rhetoric\n' +
                  '     * Vivid expression\n' +
                  '     * Elegant style\n\n' +
                  '3. Grammar and Punctuation (20 points):\n' +
                  '   - Grammar (10 points):\n' +
                  '     * Standard grammar\n' +
                  '     * Clear expression\n' +
                  '     * Logical coherence\n' +
                  '   - Punctuation (10 points):\n' +
                  '     * Accurate punctuation\n' +
                  '     * Proper formatting\n' +
                  '     * Appropriate paragraphing'
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '評分等級對照：\n' +
                  '5**（90-100分）：卓越表現，各方面均達極高水平\n' +
                  '5*（85-89分）：優異表現，各方面均達高水平\n' +
                  '5（80-84分）：良好表現，各方面均達良好水平\n' +
                  '4（70-79分）：滿意表現，各方面均達基本要求\n' +
                  '3（60-69分）：及格表現，勉強達到基本要求\n' +
                  '2（40-59分）：不及格，未能達到基本要求\n' +
                  '1（0-39分）：嚴重不足，遠低於基本要求'
                : 'Grade Scale:\n' +
                  '5** (90-100): Exceptional performance, outstanding in all aspects\n' +
                  '5* (85-89): Excellent performance, high level in all aspects\n' +
                  '5 (80-84): Good performance, good level in all aspects\n' +
                  '4 (70-79): Satisfactory performance, meets basic requirements\n' +
                  '3 (60-69): Pass performance, barely meets basic requirements\n' +
                  '2 (40-59): Fail, does not meet basic requirements\n' +
                  '1 (0-39): Severe deficiency, far below basic requirements'
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '重要提示：\n' +
                  '1. 回答必須且只能是一個有效的 JSON 對象\n' +
                  '2. 不能包含任何其他文字、前綴或說明\n' +
                  '3. 不能包含任何 JSON 相關的標記（如 json、```json 等）\n' +
                  '4. 不能包含任何格式化的前綴或後綴\n' +
                  '5. 直接返回 JSON 對象，不要有任何包裝\n' +
                  '6. 使用 \\n 作為換行符\n' +
                  '7. 確保 JSON 可以被 JSON.parse() 解析\n' +
                  '8. 分數必須是 0-100 之間的整數\n' +
                  '9. 反饋必須包含 comment、recommendation、grammarCorrections 和 fullText 四個字段\n' +
                  '10. 評分時要嚴格，不要輕易給高分，除非作文真的表現出色'
                : 'Important:\n' +
                  '1. Response must be ONLY a valid JSON object\n' +
                  '2. No additional text, prefixes, or explanations allowed\n' +
                  '3. Do not include any JSON-related markers (like json, ```json, etc.)\n' +
                  '4. Do not include any formatting prefixes or suffixes\n' +
                  '5. Return the JSON object directly, with no wrapping\n' +
                  '6. Use \\\\n for line breaks\n' +
                  '7. Ensure JSON can be parsed by JSON.parse()\n' +
                  '8. Score must be an integer between 0-100\n' +
                  '9. Feedback must include comment, recommendation, grammarCorrections, and fullText fields\n' +
                  '10. Be strict in scoring, do not give high scores unless the composition truly excels'
            },
            {
              role: "user",
              content: i18n.language === 'zh'
                ? '你的回答必須且只能是一個有效的 JSON 對象，格式如下：\n' +
                  '{\n' +
                  '  "grade": "HKDSE等級（5**, 5*, 5, 4, 3, 2, 1）",\n' +
                  '  "score": 分數（0-100的整數）,\n' +
                  '  "scoreBreakdown": {\n' +
                  '    "content": {\n' +
                  '      "theme": 分數（0-15）,\n' +
                  '      "depth": 分數（0-15）,\n' +
                  '      "structure": 分數（0-10）\n' +
                  '    },\n' +
                  '    "language": {\n' +
                  '      "vocabulary": 分數（0-15）,\n' +
                  '      "sentence": 分數（0-15）,\n' +
                  '      "rhetoric": 分數（0-10）\n' +
                  '    },\n' +
                  '    "grammar": {\n' +
                  '      "grammar": 分數（0-10）,\n' +
                  '      "punctuation": 分數（0-10）\n' +
                  '    }\n' +
                  '  },\n' +
                  '  "feedback": {\n' +
                  '    "comment": "總體評語，需包含對內容、語言表達和文法的具體評價",\n' +
                  '    "recommendation": "具體的改進建議，針對不足之處提出明確的改進方向",\n' +
                  '    "grammarCorrections": "只標記語法、用詞、錯別字、標點等錯誤的修改，使用以下格式：\\n- 刪除的內容用 ~~刪除的內容~~ 標記\\n- 添加的內容用 **添加的內容** 標記\\n- 修改的內容用 ~~原內容~~**修改後內容** 標記\\n\\n例：\\n今天天氣~~很好~~**晴朗**，我去了公園。\\n\\n注意：\\n1. 只標記需要修改的部分\\n2. 保持原文的段落結構\\n3. 確保修改後的文本符合語法",\n' +
                  '    "fullText": "完整的作文內容，包含所有修改後的內容，不需要使用任何標記。這應該是一個完整的、可以直接閱讀的文本。\\n\\n例如：\\n今天天氣晴朗，我去了公園。\\n公園裡有很多人在散步。\\n我坐在長椅上休息。\\n\\n注意：\\n1. 必須包含完整的作文內容\\n2. 所有修改都已經應用\\n3. 保持原文的段落結構\\n4. 確保文本通順且符合語法"\n' +
                  '  }\n' +
                  '}'
                : 'Your response must be ONLY a valid JSON object, with no additional text, prefixes, or explanations. The JSON object must follow this exact format:\n' +
                  '{\n' +
                  '  "grade": "HKDSE Grade (5**, 5*, 5, 4, 3, 2, 1)",\n' +
                  '  "score": score (integer between 0-100),\n' +
                  '  "scoreBreakdown": {\n' +
                  '    "content": {\n' +
                  '      "theme": score (0-15),\n' +
                  '      "depth": score (0-15),\n' +
                  '      "structure": score (0-10)\n' +
                  '    },\n' +
                  '    "language": {\n' +
                  '      "vocabulary": score (0-15),\n' +
                  '      "sentence": score (0-15),\n' +
                  '      "rhetoric": score (0-10)\n' +
                  '    },\n' +
                  '    "grammar": {\n' +
                  '      "grammar": score (0-10),\n' +
                  '      "punctuation": score (0-10)\n' +
                  '    }\n' +
                  '  },\n' +
                  '  "feedback": {\n' +
                  '    "comment": "Overall assessment, including specific evaluation of content, language expression, and grammar",\n' +
                  '    "recommendation": "Specific improvement suggestions, targeting areas that need improvement",\n' +
                  '    "grammarCorrections": "Only mark corrections for grammar, vocabulary, punctuation, etc. using the following format:\\n- Deleted content marked with ~~deleted content~~\\n- Added content marked with **added content**\\n- Modified content marked with ~~original~~**modified**\\n\\nExample:\\nOriginal: The weather was good, I went to the park.\\nModified: The weather was ~~good~~**sunny**, I went to the park.\\n\\nNote:\\n1. Only mark parts that need correction\\n2. Maintain the original paragraph structure\\n3. Ensure the modified text is grammatically correct",\n' +
                  '    "fullText": "Complete composition content with all corrections applied, without any markers. This should be a complete, readable text.\\n\\nExample:\\nThe weather was sunny, I went to the park.\\nThere were many people walking in the park.\\nI sat on a bench to rest.\\n\\nNote:\\n1. Must include the complete composition\\n2. All corrections should be applied\\n3. Maintain the original paragraph structure\\n4. Ensure the text is coherent and grammatically correct"\n' +
                  '  }\n' +
                  '}'
            }
          ],
          stream: false
        }),
      });

      if (!response.ok) {
        throw new Error(t('markingError'));
      }

      const data = await response.json();
      clearInterval(progressInterval);
      setMarkingProgress(100);

      if (data.output && data.output.content) {
        try {
          const result = JSON.parse(data.output.content.trim());
          if (typeof result.grade === 'string' && 
              typeof result.score === 'number' && 
              typeof result.scoreBreakdown === 'object' && 
              typeof result.feedback === 'object' && 
              typeof result.feedback.comment === 'string' &&
              typeof result.feedback.recommendation === 'string' &&
              typeof result.feedback.grammarCorrections === 'string' &&
              typeof result.feedback.fullText === 'string') {
            setMarkingResult(result);
            setUploadStatus({ message: t('markingComplete') });
          } else {
            throw new Error(t('invalidResponseError'));
          }
        } catch (error) {
          console.error('Parsing error:', error);
          throw new Error(t('invalidResponseError'));
        }
      } else {
        throw new Error(t('invalidResponseError'));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('markingError');
      setUploadStatus({ message: errorMessage, error: true });
      onUploadError(errorMessage);
    } finally {
      setIsMarking(false);
    }
  };

  const formatEditedText = (text: string) => {
    // First replace \\n with \n to handle encoded line breaks
    const normalizedText = text.replace(/\\n/g, '\n');
    // Unescape other special characters
    const unescapedText = normalizedText
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\\\/g, '\\');
    
    return unescapedText.split('\n').map((line, index) => {
      // Handle deleted content (~~deleted~~)
      const deletedParts = line.split(/(~~.*?~~)/);
      return (
        <div key={index} style={{ marginBottom: '8px' }}>
          {deletedParts.map((part, i) => {
            if (part.startsWith('~~') && part.endsWith('~~')) {
              return (
                <span key={i} style={{ textDecoration: 'line-through', color: '#dc2626' }}>
                  {part.slice(2, -2)}
                </span>
              );
            }
            // Handle added content (**added**)
            const addedParts = part.split(/(\*\*.*?\*\*)/);
            return (
              <span key={i}>
                {addedParts.map((subPart, j) => {
                  if (subPart.startsWith('**') && subPart.endsWith('**')) {
                    return (
                      <span key={j} style={{ color: '#059669', fontWeight: 'bold' }}>
                        {subPart.slice(2, -2)}
                      </span>
                    );
                  }
                  return subPart;
                })}
              </span>
            );
          })}
        </div>
      );
    });
  };

  const formatFeedback = (feedback: { comment: string; recommendation: string }) => {
    // Normalize line breaks
    const normalizedComment = feedback.comment.replace(/\\n/g, '\n');
    const normalizedRecommendation = feedback.recommendation.replace(/\\n/g, '\n');

    return (
      <>
        <div style={{ 
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          <strong style={{ 
            fontSize: '16px', 
            color: '#1e293b',
            display: 'block',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            {t('comment')}
          </strong>
          <div style={{ 
            color: '#334155',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap' 
          }}>
            {normalizedComment}
          </div>
        </div>
        <div style={{ 
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        }}>
          <strong style={{ 
            fontSize: '16px', 
            color: '#1e293b',
            display: 'block',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            {t('recommendation')}
          </strong>
          <div style={{ 
            color: '#334155',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap' 
          }}>
            {normalizedRecommendation}
          </div>
        </div>
      </>
    );
  };

  const formatComparisonText = (originalText: string, editedText: string) => {
    const originalLines = originalText.split('\n');
    const editedLines = editedText.split('\n');
    
    return (
      <ComparisonContainer>
        <ComparisonColumn>
          <ComparisonTitle>{t('originalText')}</ComparisonTitle>
          <ComparisonContent>
            {originalLines.map((line, index) => (
              <div key={index} style={{ marginBottom: '8px' }}>{line}</div>
            ))}
          </ComparisonContent>
        </ComparisonColumn>
        <ComparisonColumn>
          <ComparisonTitle>{t('editedText')}</ComparisonTitle>
          <ComparisonContent>
            {editedLines.map((line, index) => {
              // Handle deleted content (~~deleted~~)
              const deletedParts = line.split(/(~~.*?~~)/);
              return (
                <div key={index} style={{ marginBottom: '8px' }}>
                  {deletedParts.map((part, i) => {
                    if (part.startsWith('~~') && part.endsWith('~~')) {
                      return (
                        <span key={i} style={{ textDecoration: 'line-through', color: '#dc2626' }}>
                          {part.slice(2, -2)}
                        </span>
                      );
                    }
                    // Handle added content (**added**)
                    const addedParts = part.split(/(\*\*.*?\*\*)/);
                    return (
                      <span key={i}>
                        {addedParts.map((subPart, j) => {
                          if (subPart.startsWith('**') && subPart.endsWith('**')) {
                            return (
                              <span key={j} style={{ color: '#059669', fontWeight: 'bold' }}>
                                {subPart.slice(2, -2)}
                              </span>
                            );
                          }
                          return subPart;
                        })}
                      </span>
                    );
                  })}
                </div>
              );
            })}
          </ComparisonContent>
        </ComparisonColumn>
      </ComparisonContainer>
    );
  };

  return (
    <UploadContainer>
      <UploadIcon>
        {getStageIcon()}
      </UploadIcon>
      <UploadTitle>{getStageTitle()}</UploadTitle>
      <UploadDescription>
        {getStageDescription()}
      </UploadDescription>
      {!uploadedFileUrl && (
        <>
          <FileInput
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,image/*"
          />
          <UploadButton
            onClick={() => fileInputRef.current?.click()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isUploading || isProcessing}
          >
            {isUploading || isProcessing ? (
              <>
                <FaSpinner className="fa-spin" />
                {isUploading ? t('uploading') : t('processing')}
              </>
            ) : (
              t('selectAndUpload')
            )}
          </UploadButton>
        </>
      )}
      {(isUploading || isProcessing) && (
        <UploadStatus>
          <ProgressBar>
            <ProgressFill
              initial={{ width: 0 }}
              animate={{ width: `${isUploading ? uploadProgress : processingProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </ProgressBar>
          <StatusText>
            {isUploading ? `${t('uploading')} ${uploadProgress}%` : `${t('processing')} ${processingProgress}%`}
          </StatusText>
        </UploadStatus>
      )}
      {uploadStatus && uploadStatus.error && (
        <StatusMessage error={uploadStatus.error}>
          <FaTimes />
          {uploadStatus.message}
        </StatusMessage>
      )}
      {uploadedFileUrl && !isProcessing && (
        <>
          <DownloadLink 
            onClick={async (e) => {
              e.preventDefault();
              try {
                const response = await fetch(`${API_ENDPOINTS.DOWNLOAD}?bucket=${uploadedFileUrl.split('/')[3]}&path=${uploadedFileUrl.split('/').slice(4).join('/')}`, {
                  headers: {
                    ...getAuthHeaders()
                  }
                });
                if (!response.ok) throw new Error('Failed to download file');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = uploadedFileUrl.split('/').pop() || 'downloaded-file';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error('Download error:', error);
                setUploadStatus({ message: t('downloadError'), error: true });
              }
            }}
            href="#"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaDownload />
            {t('viewFile')}
          </DownloadLink>
          <TextContainer>
            <TextLabel>
              <FaFileAlt />
              {t('yourWriting')}
            </TextLabel>
            <div style={{
              marginBottom: '16px',
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <strong style={{ 
                  fontSize: '15px', 
                  color: '#1e293b',
                  minWidth: '80px'
                }}>
                  {t('compositionTopic')}:
                </strong>
                <input
                  type="text"
                  value={compositionTopic}
                  onChange={(e) => setCompositionTopic(e.target.value)}
                  placeholder={t('noTopic')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '15px',
                    backgroundColor: '#f8fafc',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            <EditableTextContent
              value={editedText || processedText.join('\n\n')}
              onChange={handleTextChange}
              placeholder={t('editPlaceholder')}
            />
            <SubmitButton
              onClick={handleSubmit}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isMarking}
            >
              {isMarking ? (
                <>
                  <FaSpinner className="fa-spin" />
                  {t('marking')}
                </>
              ) : (
                t('submitForMarking')
              )}
            </SubmitButton>
          </TextContainer>
          {markingResult && (
            <TextContainer style={{ marginTop: '20px' }}>
              <TextLabel>
                {t('markingResult')}
              </TextLabel>
              <TextContent>
                <GradeScoreContainer>
                  <GradeScoreHeader>
                    <GradeScoreTitle>{t('markingResult')}</GradeScoreTitle>
                  </GradeScoreHeader>
                  <GradeScoreContent>
                    <GradeScoreItem>
                      <GradeScoreLabel>{t('grade')}</GradeScoreLabel>
                      <GradeScoreValue isGrade>{markingResult.grade}</GradeScoreValue>
                    </GradeScoreItem>
                    <GradeScoreItem>
                      <GradeScoreLabel>{t('score')}</GradeScoreLabel>
                      <GradeScoreValue>
                        {markingResult.score}
                        <ScoreMax>/100</ScoreMax>
                      </GradeScoreValue>
                    </GradeScoreItem>
                  </GradeScoreContent>
                </GradeScoreContainer>
                
                <ScoreBreakdownContainer>
                  <ScoreCategory>
                    <CategoryTitle>{t('scoreBreakdown.content.title')}</CategoryTitle>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.content.theme')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.content.theme} max={15}>
                        {markingResult.scoreBreakdown.content.theme}/15
                      </ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.content.depth')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.content.depth} max={15}>
                        {markingResult.scoreBreakdown.content.depth}/15
                      </ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.content.structure')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.content.structure} max={10}>
                        {markingResult.scoreBreakdown.content.structure}/10
                      </ScoreValue>
                    </ScoreItem>
                  </ScoreCategory>

                  <ScoreCategory>
                    <CategoryTitle>{t('scoreBreakdown.language.title')}</CategoryTitle>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.language.vocabulary')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.language.vocabulary} max={15}>
                        {markingResult.scoreBreakdown.language.vocabulary}/15
                      </ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.language.sentence')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.language.sentence} max={15}>
                        {markingResult.scoreBreakdown.language.sentence}/15
                      </ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.language.rhetoric')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.language.rhetoric} max={10}>
                        {markingResult.scoreBreakdown.language.rhetoric}/10
                      </ScoreValue>
                    </ScoreItem>
                  </ScoreCategory>

                  <ScoreCategory>
                    <CategoryTitle>{t('scoreBreakdown.grammar.title')}</CategoryTitle>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.grammar.grammar')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.grammar.grammar} max={10}>
                        {markingResult.scoreBreakdown.grammar.grammar}/10
                      </ScoreValue>
                    </ScoreItem>
                    <ScoreItem>
                      <ScoreLabel>{t('scoreBreakdown.grammar.punctuation')}</ScoreLabel>
                      <ScoreValue score={markingResult.scoreBreakdown.grammar.punctuation} max={10}>
                        {markingResult.scoreBreakdown.grammar.punctuation}/10
                      </ScoreValue>
                    </ScoreItem>
                  </ScoreCategory>
                </ScoreBreakdownContainer>
                <div style={{ marginTop: '24px' }}>
                  {formatFeedback(markingResult.feedback)}
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '8px',
                    textAlign: 'left',
                    lineHeight: '1.6',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}>
                    <strong style={{ 
                      fontSize: '16px', 
                      color: '#1e293b',
                      display: 'block',
                      marginBottom: '12px',
                      textAlign: 'center'
                    }}>
                      {t('grammarCorrections')}
                    </strong>
                    {formatEditedText(markingResult.feedback.grammarCorrections)}
                  </div>
                </div>
                <div style={{ marginTop: '24px' }}>
                  <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#f8fafc', 
                    borderRadius: '8px',
                    textAlign: 'left',
                    lineHeight: '1.6',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}>
                    <strong style={{ 
                      fontSize: '16px', 
                      color: '#1e293b',
                      display: 'block',
                      marginBottom: '12px',
                      textAlign: 'center'
                    }}>
                      {t('editedVersion')}
                    </strong>
                    {formatEditedText(markingResult.feedback.fullText)}
                  </div>
                </div>
              </TextContent>
            </TextContainer>
          )}
        </>
      )}
      {isMarking && (
        <UploadStatus>
          <ProgressBar>
            <ProgressFill
              initial={{ width: 0 }}
              animate={{ width: `${markingProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </ProgressBar>
          <StatusText>
            {t('markingStatus')} {markingProgress}%
          </StatusText>
        </UploadStatus>
      )}
    </UploadContainer>
  );
};

export default FileUpload; 